import { forwardRef } from 'react';
import { Container, ContainerProps } from './container';
import { formContainerStyles } from './commonStyles';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Props for the FormContainer component.
 * Extends ContainerProps but omits maxWidth since FormContainer uses fixed positioning.
 */
export type FormContainerProps = Omit<ContainerProps, 'maxWidth'>;

/**
 * FormContainer - Fixed position container for form overlays.
 *
 * A pre-configured Container for form pages that need fixed positioning below
 * a header. The container is positioned fixed with a top margin equal to the
 * standard header height (64px).
 *
 * **When to use:**
 * - Asset creation forms (NewPage)
 * - Asset editing forms (RecordEditablePage)
 * - Any form that appears as an overlay over map/inventory
 *
 * **Pattern from:** `sharedStyles['form-container']`
 *
 * **Note:** This component uses `position: fixed`, so it will be removed from
 * the normal document flow. Ensure your layout accounts for this.
 *
 * @example
 * ```tsx
 * import { FormContainer } from '@planitgeo/treeplotter-ui-library';
 *
 * function NewAssetPage() {
 *   return (
 *     <FormContainer>
 *       <AssetForm />
 *     </FormContainer>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With className for styling/testing
 * <FormContainer className="asset-edit-form">
 *   <RecordEditableForm />
 * </FormContainer>
 * ```
 *
 * @example
 * ```tsx
 * // With additional sx styles
 * <FormContainer sx={{ bgcolor: 'background.paper', boxShadow: 3 }}>
 *   <Form />
 * </FormContainer>
 * ```
 */
export const FormContainer = forwardRef<HTMLDivElement, FormContainerProps>(
  ({ sx, ...props }, ref) => {
    // Merge the base styles with any custom sx props
    const mergedSx: SxProps<Theme> = sx ? [formContainerStyles, sx].flat() : formContainerStyles;

    return <Container ref={ref} maxWidth={false} sx={mergedSx} {...props} />;
  }
);

FormContainer.displayName = 'FormContainer';
